// data.ts - the locked dx5 Diagnose teaching hand, street by street. Transcribed 1:1 from the approved
// mockup (docs/ui-explorations/local-mode-read-first-v2.html, the DSTREETS array). Same teaching hand as
// Observe (A-J suited in the Cutoff vs a Vacuum who leads into hero), but now YOU make the read and the
// decision. Authored teaching content, sanctioned by PRODUCT_NORTH_STAR §5 (same sanction as
// engine/bridge.ts's felt cards).
//
// S21 owns the INTERACTION (read lock-in, the two-tier input ramp, the fading math aid, the app-shown
// price). The grade reveal and its coaching payload are S22 - deliberately NOT transcribed here.
import type { ArchetypeId } from '@postflop/schema';

/** One run of price-explanation text. A plain string is prose; `{term}` is a tap-to-learn trigger.
 *  (Mirrors the Observe token layer so poker terms in the math aid stay tap-to-learn.) */
export type PriceToken = string | { term: string; text: string };

/** One of the five hand families the user weights the villain into. Weight is captured in view state
 *  (0..3 = none/rare/some/a lot); this is just the family's label + one-line detail. */
export interface FamilyDef {
  name: string;
  detail: string;
}

/** One multiple-choice action button (the on-ramp tier). `cls` drives the fold/call/raise styling. */
export interface ActionDef {
  key: 'fold' | 'call' | 'raise';
  label: string;
  sub: string;
  cls: 'fold' | 'call' | 'raise';
}

/** One quick-size chip in the bet-math builder: the dollar amount + its pot-relative shorthand. */
export interface QuickSize {
  amount: string;
  ratio: string;
}

/** The manual bet-math tier for a street. NOTHING here pre-fills the user's number - the input starts
 *  empty and the quick chips only fill on tap. Null when the street has no raise (river = Fold/Call). */
export interface BetMathDef {
  pfx: string;
  pot: string;
  call: string;
  callLabel: string;
  vbet: string;
  sub: string;
  quick: QuickSize[];
}

/** The app-shown price + its fading math aid for one street. `big` is the headline the app always shows
 *  (a required-equity % postflop, "Iso" preflop). `full` is the worked explanation; `shorthand` is the
 *  pot-odds trick; "off" shows the headline alone (see <MathAid>). */
export interface PriceDef {
  title: string;
  big: string;
  full: PriceToken[];
  shorthand: string;
}

/** Everything the Diagnose view renders for one street (S21 scope - no grade payload). */
export interface DiagnoseStreetData {
  id: 'preflop' | 'flop' | 'turn' | 'river';
  kicker: string;
  h2: string;
  lede: string;
  /** the "tag the player" section subhead for this street. */
  tagSub: string;
  /** null preflop (no board yet, so no hand-family weighting). */
  families: FamilyDef[] | null;
  price: PriceDef;
  actions: ActionDef[];
  /** null when the street has no raise option (river). */
  bet: BetMathDef | null;
}

/** The player-tag choices (mockup DTAGS). Display nicknames are UI-only teaching labels; the archetype
 *  ids they map to are what S22's engine/diagnosis grader will consume (null = "Not sure yet"). */
export const PLAYER_TAGS = ['Rock', 'Vacuum', 'Firecracker', 'Whale', 'Not sure yet'] as const;
export type PlayerTag = (typeof PLAYER_TAGS)[number];

/** Nickname → scripted archetype id (forward-compat capture for S22; not graded in S21). Vacuum maps to
 *  `sticker` to match engine/bridge.ts's teaching-table villains. */
export const TAG_ARCHETYPE: Record<PlayerTag, ArchetypeId | null> = {
  Rock: 'rock',
  Vacuum: 'sticker',
  Firecracker: 'firestarter',
  Whale: 'tourist',
  'Not sure yet': null,
};

/** The inverse of `TAG_ARCHETYPE`, for painting a PERSISTED seat read back onto the felt (TB3). Total over
 *  everything storable: `TAG_ARCHETYPE` only ever yields these four ids, so a stored read always has a
 *  nickname to render. Derived from the map above so the two can never drift apart. */
export const ARCHETYPE_TAG: Partial<Record<ArchetypeId, PlayerTag>> = Object.fromEntries(
  (Object.entries(TAG_ARCHETYPE) as [PlayerTag, ArchetypeId | null][])
    .filter((e): e is [PlayerTag, ArchetypeId] => e[1] !== null)
    .map(([tag, id]) => [id, tag]),
);

/** The five hand families (identical across postflop streets in this teaching hand). */
const FAMILIES: FamilyDef[] = [
  { name: 'Nuts', detail: 'sets, two pair' },
  { name: 'Strong made', detail: 'a better jack' },
  { name: 'Medium pairs', detail: '8x, weak jack' },
  { name: 'Draws', detail: 'gutshot, overs' },
  { name: 'Air, bluff', detail: 'pure nothing' },
];

export const DIAGNOSE_STREETS: DiagnoseStreetData[] = [
  {
    id: 'preflop',
    kicker: 'Diagnose it yourself',
    h2: 'A Vacuum limped on your right',
    lede: 'Nothing is filled in. Read the seat, then decide how to play A-J suited.',
    tagSub: 'The player on your right just limped in for the minimum. What kind of player is he?',
    families: null,
    price: {
      title: 'The plan',
      big: 'Iso',
      full: [
        'No price to pay yet, you are first to raise. Against one limper on a loose table you want to ',
        { term: 'isolate', text: 'isolate' },
        ': raise big enough to get one caller, not five.',
      ],
      shorthand: 'First in, no price. Raise to isolate, about 5 to 6 big blinds.',
    },
    actions: [
      { key: 'fold', label: 'Fold', sub: 'give up A-J suited', cls: 'fold' },
      { key: 'call', label: 'Just call', sub: 'see a cheap flop', cls: 'call' },
      { key: 'raise', label: 'Raise to isolate', sub: 'build a heads-up pot', cls: 'raise' },
    ],
    bet: {
      pfx: 'Raise to',
      pot: '$7',
      call: '$3',
      callLabel: 'To call',
      vbet: 'limp $3',
      sub: 'A clean iso is about 5 to 6 big blinds. Bigger than a TV raise, because small raises get five callers.',
      quick: [
        { amount: '$12', ratio: '4x' },
        { amount: '$15', ratio: '5x' },
        { amount: '$18', ratio: '6x' },
        { amount: '$21', ratio: '7x' },
      ],
    },
  },
  {
    id: 'flop',
    kicker: 'The flop',
    h2: 'He leads $30 into $43',
    lede: 'J 8 4, dry. You hit top pair top kicker, then the Vacuum does something he never does.',
    tagSub: 'He just led into you for the first time all night. Confirm the read.',
    families: FAMILIES,
    price: {
      title: 'The price',
      big: '29%',
      full: [
        'He bet $30 into $43. Call and you risk $30 to play for a $103 pot, so you only need to be ahead about 29% of the time. That is the ',
        { term: 'reqeq', text: 'required equity' },
        '.',
      ],
      shorthand: '$30 to win a $103 pot. 30 / 103 is about 29%.',
    },
    actions: [
      { key: 'fold', label: 'Fold', sub: 'give up top pair', cls: 'fold' },
      { key: 'call', label: 'Call $30', sub: 'keep worse hands in', cls: 'call' },
      { key: 'raise', label: 'Raise', sub: 'fold out worse', cls: 'raise' },
    ],
    bet: {
      pfx: 'Raise to',
      pot: '$43',
      call: '$30',
      callLabel: 'To call',
      vbet: '$30',
      sub: 'If you raise you have to build the number. But ask first what worse hand pays it.',
      quick: [
        { amount: '$85', ratio: '2.5x' },
        { amount: '$95', ratio: '3x' },
        { amount: '$120', ratio: '4x' },
      ],
    },
  },
  {
    id: 'turn',
    kicker: 'The turn',
    h2: 'He fires again, $40 into $103',
    lede: 'The 2 is a blank. A second bet from a man who never bets is a second piece of evidence.',
    tagSub: 'Same player, betting twice now. Has your read changed?',
    families: FAMILIES,
    price: {
      title: 'The price',
      big: '22%',
      full: [
        'He bet $40 into $103. Call and you risk $40 for a $183 pot, so you need to be ahead about 22% of the time. Cheap, but the read is shifting against you.',
      ],
      shorthand: '$40 to win a $183 pot. 40 / 183 is about 22%.',
    },
    actions: [
      { key: 'fold', label: 'Fold', sub: 'too soon', cls: 'fold' },
      { key: 'call', label: 'Call $40', sub: 'plan the river', cls: 'call' },
      { key: 'raise', label: 'Raise', sub: 'turn it into a bluff', cls: 'raise' },
    ],
    bet: {
      pfx: 'Raise to',
      pot: '$103',
      call: '$40',
      callLabel: 'To call',
      vbet: '$40',
      sub: 'You could raise, but with a bluff-catcher what are you raising for?',
      quick: [
        { amount: '$110', ratio: '2.5x' },
        { amount: '$130', ratio: '3x' },
        { amount: '$160', ratio: '4x' },
      ],
    },
  },
  {
    id: 'river',
    kicker: 'The river',
    h2: 'He jams all in, $120 into $183',
    lede: 'The 7 is another blank. Every obvious draw missed. This is the decision the whole hand was building to.',
    tagSub: 'Third barrel, all in, from a lifelong caller. What is he?',
    families: FAMILIES,
    price: {
      title: 'The price',
      big: '28%',
      full: [
        'He bet $120 into $183. Call and you risk $120 for a $423 pot, so you need to be ahead about 28% of the time. Against a Vacuum who never bluffs, you are ahead far less than that.',
      ],
      shorthand: '$120 to win a $423 pot. 120 / 423 is about 28%.',
    },
    actions: [
      { key: 'fold', label: 'Fold', sub: 'trust the read', cls: 'fold' },
      { key: 'call', label: 'Call $120', sub: 'pay to see it', cls: 'call' },
    ],
    bet: null,
  },
];
